// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ascending } from "d3-array";
import {
  add,
  differenceInDays,
  format,
  parseISO,
  startOfToday,
} from "date-fns";
import { DocumentData, Timestamp } from "firebase/firestore";
import { isEmpty, pick, pickBy } from "lodash";
import { action, computed, makeObservable, when } from "mobx";

import { OpportunityType } from "~datatypes";
import { HydrationState, isHydrated } from "~hydration-utils";

import {
  reasonsIncludesOtherKey,
  StatusPalette,
} from "../../core/utils/workflowsUtils";
import {
  AutoSnoozeUpdate,
  Denial,
  ManualSnoozeUpdate,
  OfficerApprovalRequest,
  OfficerDenialRequest,
  OfficerRequest,
  OpportunityUpdate,
  OpportunityUpdateWithForm,
  SharedSnoozeUpdate,
  Submission,
  SupervisorResponse,
  UpdateLog,
} from "../../FirestoreStore";
import { RootStore } from "../../RootStore";
import { OpportunityApprovalActionsMetadata } from "../../RootStore/AnalyticsStore/AnalyticsStore";
import { formatDateToISO, toTitleCase } from "../../utils";
import {
  DocumentSubscription,
  OpportunityUpdateSubscription,
  UpdateFunction,
} from "../subscriptions";
import { JusticeInvolvedPerson } from "../types";
import {
  getPersonDaysToRelease,
  getSnoozeUntilDate,
  snoozeUntilDateInTheFuture,
} from "../utils";
import { FormBase } from "./Forms/FormBase";
import { SnoozeConfiguration } from "./OpportunityConfigurations/modules/SnoozeConfiguration/interfaces/ISnoozeConfiguration";
import {
  Component,
  DefaultEligibility,
  DenialReasonsMap,
  FormVariant,
  Opportunity,
  OpportunityBannerInfo,
  OpportunityNotificationsByPage,
  OpportunityRequirement,
  OpportunityStatus,
  OpportunityTab,
  OpportunityTabGroup,
  RevertConfirmationCopy,
} from "./types";
import { buildOpportunityCompareFunction } from "./utils/compareUtils";
import {
  hydrateReq,
  hydrateStr,
  hydrateUntypedCriteria,
  HydrationContext,
  UntypedCriteriaFormatters,
} from "./utils/criteriaUtils";
import { createNotificationId } from "./utils/notificationUtils";

export function updateOpportunityEligibility(
  opportunity: Opportunity,
  rootStore: RootStore,
) {
  return async (record: DocumentData) => {
    // If a transition has occured since the opportunity was submitted,
    // clear the submitted status.
    // Not all opportunity types populate latestTransitionDate.
    const { latestTransitionDate } = opportunity;
    const submittedDate = record.submitted?.date.toDate();
    if (
      latestTransitionDate &&
      submittedDate &&
      latestTransitionDate > submittedDate
    ) {
      rootStore.firestoreStore.deleteOpportunitySubmitted(opportunity);
    }

    // If the record is eligible, then no update is needed.
    const denialReasons = record.denial?.reasons ?? [];
    if (!denialReasons.length || (!record.autoSnooze && !record.manualSnooze)) {
      return;
    }

    const snoozeUntilDate = getSnoozeUntilDate({
      ...(record.manualSnooze ?? {}),
      ...(record.autoSnooze ?? {}),
    });

    if (!snoozeUntilDate) return;

    if (denialReasons.length > 0 && snoozeUntilDateInTheFuture(snoozeUntilDate))
      return;

    // If there are denial reasons and the opp should be resurfaced, reset the
    // denial reasons and the manual and auto snooze configs.
    await rootStore.firestoreStore.deleteOpportunityDenialAndSnooze(
      opportunity,
    );
  };
}

/**
 * Implements functionality shared by all Opportunities, most notably the `Hydratable` interface.
 * While this is an abstract class, it provides stubs rather than abstract properties, whenever possible,
 * to facilitate incremental development of new opportunities.
 */
export class OpportunityBase<
  PersonType extends JusticeInvolvedPerson,
  ReferralRecord extends DocumentData,
  UpdateRecord extends OpportunityUpdateWithForm<any> = OpportunityUpdate,
> implements Opportunity<PersonType>
{
  form?: FormBase<any, any>;

  updatesSubscription: DocumentSubscription<UpdateRecord>;

  /**
   * If the opportunity allows external system requests
   */
  readonly supportsExternalRequest: boolean = false;

  /**
   * Updates an ineligible opportunity to be eligible when
   * the overridden/denied time period has expired.
   */
  updateOpportunityEligibility: UpdateFunction<DocumentData>;

  constructor(
    public person: PersonType,
    readonly type: OpportunityType,
    public rootStore: RootStore,
    public record: ReferralRecord,
  ) {
    makeObservable(this, {
      denial: computed,
      autoSnooze: computed,
      manualSnooze: computed,
      isSnoozed: computed,
      snoozeForDays: computed,
      manualSnoozeUntilDate: computed,
      hydrate: action,
      hydrationState: computed,
      updates: computed,
      reviewStatus: computed,
      setCompletedIfEligible: action,
      setAutoSnooze: action,
      setManualSnooze: action,
      setDenialReasons: action,
      setOtherReasonText: action,
      denied: computed,
      isSubmitted: computed,
      isPendingOverdue: computed,
      isEligibleStaleViewed: computed,
      isDenied: computed,
      isCompleted: computed,
      isInProgress: computed,
      submittedUpdate: computed,
      submittedButtonText: computed,
      undoSubmittedButtonText: computed,
      bannerInfo: computed,
      isInSupervisorReview: computed,
      isInGrantReview: computed,
      isInSnoozeReview: computed,
      latestAction: computed,
      actionHistory: computed,
      compareFunction: computed,
      currentReviewerId: computed,
      allUniqueReviewerIds: computed,
    });

    this.updateOpportunityEligibility = updateOpportunityEligibility(
      this,
      this.rootStore,
    );

    this.updatesSubscription = new OpportunityUpdateSubscription<UpdateRecord>(
      this.rootStore.firestoreStore,
      person.recordId,
      this.firestoreUpdateDocId,
      this.updateOpportunityEligibility,
    );
  }

  get firestoreUpdateDocId() {
    // For multi-instance opportunities, the document ID has the opportunity id
    // appended to the opportunity type. Otherwise, use just the opportunity type
    return this.opportunityId === undefined
      ? this.type
      : `${this.type}_${this.opportunityId}`;
  }

  get opportunityId() {
    return this.record.opportunityId;
  }

  get selectId() {
    // Used as the key for opportunity-specific components, i.e. CaseloadOpportunityCell
    // Also used when filtering on a list of opportunities.
    return (
      this.record.opportunityPseudonymizedId ?? this.person.pseudonymizedId
    );
  }

  get accordionKey() {
    // Used to control what opportunities are expanded in accordion components
    return this.opportunityId
      ? `${this.type}_${this.opportunityId}`
      : this.type;
  }

  get config() {
    return this.rootStore.workflowsRootStore.opportunityConfigurationStore
      .opportunities[this.type];
  }

  /**
   * Returns a list of undismissed notifications for each page, checking dismissal per page, then group by page
   * @returns A list of notifications for each page, checking dismissal per page, then group by page.
   * @returns undefined if there are no notifications or the opportunity is not eligible
   * @example
   * {
   *   profile: [notification1, notification2],
   *   supervisionSupervisor: [notification3]
   * }
   */
  get notificationsByPage() {
    if (!this.config.notifications || !this.isEligible || this.denied) return;

    const { dismissedOpportunityNotificationIds } =
      this.rootStore.workflowsStore;
    // Use Set for O(1) lookup instead of O(n) array includes
    const dismissedIdsSet = new Set(dismissedOpportunityNotificationIds);

    // Create list of notifications for each page, checking dismissal per page, then group by page
    const notificationsByPage: OpportunityNotificationsByPage = {};

    /**
     * Context is needed to hydrate the notification body and title.
     */
    const context: HydrationContext = {
      criteria: {},
      formatters: this.criteriaFormatters ?? {},
      opportunity: this,
    };

    this.config.notifications.forEach((n) => {
      n.pages.forEach((page) => {
        const id = createNotificationId(n.id, this, page);
        // if the notification is dismissed or the page is caseload, skip adding it to the list of notifications for the page
        if (dismissedIdsSet.has(id) || page === "caseload") return;
        // if the page is not in the notificationsByPage object, create an empty array for the page
        notificationsByPage[page] ??= [];

        // add the new notification to the list of notifications for the page
        notificationsByPage[page]?.push({
          ...n,
          id,
          body: hydrateStr(n.body, context),
          title: n.title ? hydrateStr(n.title, context) : undefined,
          cta: n.cta ? hydrateStr(n.cta, context) : undefined,
          link: this.person.profileUrl,
        });
      });
    });

    // if there are any notifications, return the notificationsByPage object, otherwise return undefined
    return Object.keys(notificationsByPage).length > 0
      ? notificationsByPage
      : undefined;
  }

  get supportsDenial(): boolean {
    return Object.keys(this.config.denialReasons).length > 0;
  }

  get updates(): UpdateRecord | undefined {
    return this.updatesSubscription.data;
  }

  get denial(): Denial | undefined {
    if (this.updates?.denial?.reasons?.length) {
      return this.updates?.denial;
    }
  }

  get denialReasons() {
    return this.config.denialReasons;
  }

  get manualSnooze(): ManualSnoozeUpdate | undefined {
    if (this.updates?.manualSnooze?.snoozedBy) {
      return this.updates?.manualSnooze;
    }
  }

  get autoSnooze(): AutoSnoozeUpdate | undefined {
    if (this.updates?.autoSnooze?.snoozedBy) {
      return this.updates?.autoSnooze;
    }
  }

  get snoozedBy(): SharedSnoozeUpdate["snoozedBy"] | undefined {
    if (this.manualSnooze) return this.manualSnooze.snoozedBy;
    if (this.autoSnooze) return this.autoSnooze.snoozedBy;
    if (this.isIndefinitelySnoozed) {
      return this.denial?.updated?.by;
    }
  }

  get submittedButtonText(): string {
    return `Mark ${this.config.submittedTabTitle}`;
  }

  get undoSubmittedButtonText(): string {
    return `Revert from ${this.config.submittedTabTitle}`;
  }

  get snoozedOnDate(): Date | undefined {
    if (this.manualSnooze) return parseISO(this.manualSnooze.snoozedOn);
    if (this.autoSnooze) return parseISO(this.autoSnooze.snoozedOn);
    if (this.isIndefinitelySnoozed) {
      return this.denial?.updated?.date.toDate();
    }
  }

  get manualSnoozeUntilDate(): Date | undefined {
    if (!this.manualSnooze || !this.snoozedOnDate) return;
    return add(this.snoozedOnDate, {
      days: this.manualSnooze.snoozeForDays,
    });
  }

  /* Returns the current number of days an opportunity will be snoozed for, taking into account the current date */
  get snoozeForDays(): number | undefined {
    if (!this.manualSnoozeUntilDate) return;
    return differenceInDays(this.manualSnoozeUntilDate, startOfToday());
  }

  get submittedUpdate(): Submission | undefined {
    return this.updates?.submitted;
  }

  get isSubmitted(): boolean {
    return this.config.supportsSubmitted && !!this.submittedUpdate;
  }

  get isPendingOverdue(): boolean {
    if (!this.isSubmitted) return false;
    const threshold = this.config.pendingOverdueDaysThreshold;
    if (threshold == null) return false;
    const submittedDate = this.submittedUpdate?.date?.toDate();
    if (!submittedDate) return false;
    return differenceInDays(startOfToday(), submittedDate) >= threshold;
  }

  get isEligibleStaleViewed(): boolean {
    const threshold = this.config.eligibleNotViewedDaysThreshold;
    if (threshold == null) return false;
    if (this.tabTitle() !== "Eligible Now") return false;

    // Fall back to eligibilityDate so never-viewed cases still count as stale.
    const clockStart = this.lastViewed?.date?.toDate() ?? this.eligibilityDate;
    if (!clockStart) return false;

    return differenceInDays(startOfToday(), clockStart) >= threshold;
  }

  get isCompleted(): boolean {
    return !!this.updates?.completed;
  }

  get isDenied(): boolean {
    return !!this.denial;
  }

  get isInProgress(): boolean {
    return !!this.lastViewed || !!this.updates?.referralForm;
  }

  get isSnoozed(): boolean {
    const snoozeUntil =
      (this.autoSnooze?.snoozeUntil &&
        parseISO(this.autoSnooze?.snoozeUntil)) ??
      this.manualSnoozeUntilDate;

    return !!snoozeUntil && snoozeUntilDateInTheFuture(snoozeUntil);
  }

  get omsSnoozeStatus() {
    return this.updates?.omsSnooze?.status;
  }

  get lastViewed(): UpdateLog | undefined {
    return this.updates?.lastViewed;
  }

  setLastViewed(): void {
    when(
      () => isHydrated(this),
      () => {
        // ignore recidiviz admins and other non-state actors in prod
        if (
          import.meta.env.VITE_DEPLOY_ENV === "production" &&
          this.rootStore.userStore.stateCode !== this.person.stateCode
        )
          return;

        this.rootStore.firestoreStore.updateOpportunityLastViewed(
          this.currentUserEmail,
          this,
        );
      },
    );
  }

  get reviewStatus(): OpportunityStatus {
    switch (true) {
      case this.isDenied:
        return "DENIED";

      case this.isCompleted:
        return "COMPLETED";

      case this.isSubmitted:
        return "SUBMITTED";

      case this.isInRevisionsRequested:
        return "REVISIONS_REQUESTED";

      case this.isGrantApproved:
        return "GRANT_APPROVED";

      case this.isInGrantReview:
        return "GRANT_REVIEW";

      case this.isInSnoozeReview:
        return "SNOOZE_REVIEW";

      case this.isInProgress:
        return "IN_PROGRESS";

      case this.almostEligible:
        return "ALMOST";
    }
    return "PENDING";
  }

  readonly defaultEligibility: DefaultEligibility = "ELIGIBLE";

  setCompletedIfEligible(): void {
    when(
      () => isHydrated(this),
      () => {
        const { pseudonymizedId } = this.person;
        const { reviewStatus } = this;
        if (reviewStatus === "DENIED" || reviewStatus === "COMPLETED") return;

        this.rootStore.firestoreStore.updateOpportunityCompleted(
          this.currentUserEmail,
          this,
        );
        this.rootStore.analyticsStore.trackSetOpportunityStatus({
          justiceInvolvedPersonId: pseudonymizedId,
          status: "COMPLETED",
          opportunityType: this.type,
          opportunityId: this.sentryTrackingId,
        });
      },
    );
  }

  get currentUserEmail(): string {
    // use || instead of ?? so if userEmail is "", we fall back to "user"
    return this.rootStore.userStore.userEmail || "user";
  }

  /**
   * Text displayed as a header when this opportunity is previewed
   */
  get previewBannerText(): string | undefined {
    return;
  }

  /**
   * Information shown in the OpportunityBanner.
   * The banner will only be displayed when previewBannerText is defined.
   * OpportunityBase.previewBannerText is undefined by default and overridden in child
   * opportunity classes with the relevant text for the opportunities where the banner
   * is to be displayed.
   */
  get bannerInfo(): OpportunityBannerInfo | undefined {
    return this.previewBannerText
      ? {
          previewBannerText: this.previewBannerText,
          link: this.person.profileUrl,
          linkText: `See ${toTitleCase(this.rootStore.workflowsStore.justiceInvolvedPersonTitle)} Profile`,
          onLinkClick: () =>
            this.rootStore.analyticsStore.trackNavigateToPersonProfileLinkClicked(
              {
                justiceInvolvedPersonId: this.person.pseudonymizedId,
                opportunityType: this.type,
              },
            ),
        }
      : undefined;
  }

  /**
   * Opportunity hydration covers only the updates subscription:
   * The opportunity record is provided to the constructor, and the form
   * is hydrated separately.
   */
  get hydrationState(): HydrationState {
    return this.updatesSubscription.hydrationState;
  }

  hydrate(): void {
    this.updatesSubscription.hydrate();
  }

  async deleteSubmitted(): Promise<void> {
    await this.rootStore.firestoreStore.deleteOpportunitySubmitted(this);

    this.rootStore.analyticsStore.trackOpportunityUnsubmitted({
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
    });
  }

  async deleteOpportunityDenialAndSnooze(): Promise<void> {
    await this.rootStore.firestoreStore.deleteOpportunityDenialAndSnooze(this);

    this.rootStore.analyticsStore.trackSetOpportunityStatus({
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      status: this.reviewStatus,
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
    });
  }

  async setManualSnooze(days: number, reasons: string[]): Promise<void> {
    // If there are no denial reasons selected, clear the snooze values
    const deleteSnoozeField = reasons.length === 0;

    this.rootStore.analyticsStore.trackOpportunitySnoozed({
      opportunityType: this.type,
      opportunityStatus: this.reviewStatus,
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      snoozeForDays: days,
      reasons,
      opportunityId: this.sentryTrackingId,
    });

    // If someone goes from being submitted to being snoozed,
    // they should no longer be submitted
    if (this.isSubmitted) {
      await this.deleteSubmitted();
    }

    await this.rootStore.firestoreStore.updateOpportunityManualSnooze(
      this,
      {
        snoozedBy: this.currentUserEmail,
        snoozedOn: format(new Date(), "yyyy-MM-dd"),
        snoozeForDays: days,
      },
      deleteSnoozeField,
    );
  }

  async setAutoSnooze(
    autoSnoozeParams: NonNullable<SnoozeConfiguration["autoSnoozeParams"]>,
    reasons: string[],
  ): Promise<void> {
    // If there are no denial reasons selected, clear the snooze values
    const deleteSnoozeField = reasons.length === 0;

    const snoozeUntil = autoSnoozeParams(startOfToday(), this);

    this.rootStore.analyticsStore.trackOpportunitySnoozed({
      opportunityType: this.type,
      opportunityStatus: this.reviewStatus,
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      snoozeUntil: formatDateToISO(snoozeUntil),
      reasons,
      opportunityId: this.sentryTrackingId,
    });

    // If someone goes from being submitted to being snoozed,
    // they should no longer be submitted
    if (this.isSubmitted) {
      await this.deleteSubmitted();
    }

    await this.rootStore.firestoreStore.updateOpportunityAutoSnooze(
      this,
      {
        snoozedBy: this.currentUserEmail,
        snoozedOn: format(new Date(), "yyyy-MM-dd"),
        snoozeUntil: formatDateToISO(snoozeUntil),
      },
      deleteSnoozeField,
    );
  }

  sortByEligibilityDate(other: Opportunity): number {
    return ascending(this.eligibilityDate, other.eligibilityDate);
  }

  compare(other: Opportunity): number {
    return this.compareFunction(this, other);
  }

  get compareFunction(): (a: Opportunity, b: Opportunity) => number {
    const { compareBy, systemType } = this.config ?? {};
    if (compareBy) return buildOpportunityCompareFunction(compareBy);
    const sortParams = [{ field: "eligibilityDate" }];

    if (systemType === "INCARCERATION")
      sortParams.push({ field: "releaseDate" });
    else sortParams.push({ field: "expirationDate" });

    return buildOpportunityCompareFunction(sortParams);
  }

  async markSubmitted(
    stateCode: string,
    newSubcategory?: string,
  ): Promise<void> {
    await this.rootStore.firestoreStore.updateOpportunitySubmitted(
      this.currentUserEmail,
      this,
      stateCode,
      newSubcategory,
    );

    this.rootStore.analyticsStore.trackOpportunityMarkedSubmitted({
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
    });

    // If someone is now submitted, they should not be denied/snoozed
    if (this.denial) {
      await this.deleteOpportunityDenialAndSnooze();
    }
  }

  /**
   * Mark this opportunity as Submitted. If newSubcategory is provided, switch this
   * opportunity to that subcategory. Return a message that can be displayed to the user
   * about the status change (this method does not itself handle displaying the message)
   * or return undefined if the opportunity's status did not change.
   */
  async markSubmittedAndGenerateToast(
    newSubcategory?: string,
    customToast?: string,
  ): Promise<string | undefined> {
    // Do nothing if this opportunity doesn't support the submitted status
    if (!this.config.supportsSubmitted) return;

    // Return no toast if marking the opportunity as submitted would do nothing,
    // or throw an error in egregious cases
    if (newSubcategory) {
      // trying to submit a submitted opportunity with its current subcategory
      // (we cannot just check if the opportunity is submitted because it is valid to
      // change the subcategory of an opportunity within the submitted status)
      if (newSubcategory === this.subcategory) return;

      // provided a subcategory that isn't valid for the submitted status
      if (!this.submitMenuOptions) {
        throw new Error(
          `Tried to mark as ${newSubcategory}, but there are no subcategories of ${this.submittedTabTitle} for this opportunity type`,
        );
      }
      if (!this.submitMenuOptions.includes(newSubcategory)) {
        throw new Error(
          `Tried to mark as ${newSubcategory}, but the only valid subcategories are ${this.submitMenuOptions}`,
        );
      }
    } else {
      // trying to submit an already submitted opportunity
      if (this.isSubmitted) return;
      // not provided a subcategory, but submitted opportunities have subcategories
      if (this.submitMenuOptions) {
        throw new Error(
          `Tried to mark as ${this.submittedTabTitle}, but a subcategory needed to be provided for this opportunity type`,
        );
      }
    }

    await this.markSubmitted(this.person.stateCode, newSubcategory);

    const toastStatus = newSubcategory
      ? this.subcategoryHeadingFor(newSubcategory)
      : this.submittedTabTitle;

    if (customToast) {
      return customToast;
    }

    return newSubcategory
      ? `Marked ${this.person.displayName} as ${this.submittedTabTitle}:"${toastStatus}" for ${this.config.label}`
      : `Marked ${this.person.displayName} as "${toastStatus}" for ${this.config.label}`;
  }

  async setDenialReasons(
    reasons: string[],
    updatedUserInput?: Record<string, string>,
  ): Promise<void> {
    if (reasons.length === 0) {
      // If the reasons are empty, this is equivalent to deleting the denial
      await this.deleteOpportunityDenialAndSnooze();
      return;
    }

    const { pseudonymizedId } = this.person;

    // If someone goes from being submitted to being denied,
    // they should no longer be submitted
    if (this.isSubmitted) {
      await this.deleteSubmitted();
    }

    // clear irrelevant reason text if necessary
    const deletions = {
      otherReason: !reasonsIncludesOtherKey(reasons),
      userInput: isEmpty(updatedUserInput),
    };

    await this.rootStore.firestoreStore.updateOpportunityDenial(
      this.currentUserEmail,
      this,
      this.person.stateCode,
      { reasons, userInput: updatedUserInput },
      deletions,
    );

    await this.rootStore.firestoreStore.updateOpportunityCompleted(
      this.currentUserEmail,
      this,
      true,
    );

    this.rootStore.analyticsStore.trackSetOpportunityStatus({
      justiceInvolvedPersonId: pseudonymizedId,
      status: "DENIED",
      opportunityType: this.type,
      deniedReasons: reasons,
      opportunityId: this.sentryTrackingId,
    });

    // Snoozing ends the approval lifecycle, so we'll mark the action history stale.
    if (this.latestAction) {
      await this.markActionHistoryStale();
    }
  }

  async setOtherReasonText(otherReason?: string): Promise<void> {
    await this.rootStore.firestoreStore.updateOpportunityDenial(
      this.currentUserEmail,
      this,
      this.person.stateCode,
      {
        otherReason,
      },
    );
  }

  trackListViewed(): void {
    const { selectedSearchIds } = this.rootStore.workflowsStore.searchStore;

    // These are all of the possible search id values by search field, even if that search id value is not in selectedSearchIds,
    // broken out by searchField.
    Object.entries(this.person.searchIdValuesBySearchField).forEach(
      ([searchField, searchIdValues]) => {
        // These are all of the search Id values for a person, that are also present in selectedSearchIds
        const activeSearchIdValues = searchIdValues.filter((id) =>
          selectedSearchIds?.includes(id),
        );

        const modifiedSearchIds = activeSearchIdValues.map((value) => {
          // Remove the CRC prefix from facility id if it exists
          const withoutPrefix =
            this.rootStore.currentTenantId === "US_ID"
              ? value.replace(/^CRC /, "")
              : value;
          // Pseudonymize staff IDs
          return withoutPrefix === this.person.assignedStaffId
            ? this.person.assignedStaffPseudoId
            : withoutPrefix;
        });

        // For each searchField, if the person matches based on that searchField fire the event
        if (modifiedSearchIds.length > 0) {
          this.rootStore.analyticsStore.trackSurfacedInList({
            justiceInvolvedPersonId: this.person.pseudonymizedId,
            opportunityType: this.type,
            searchIdValue: modifiedSearchIds.join(","),
            searchField: searchField,
            tabTitle: this.tabTitle(),
            opportunityId: this.sentryTrackingId,
          });
        }
      },
    );
  }

  trackPreviewed(): void {
    this.rootStore.analyticsStore.trackOpportunityPreviewed({
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
      tabTitle: this.tabTitle(),
    });
  }

  get subcategory(): string | undefined {
    return undefined;
  }

  subcategoryHeadingFor(subcategory: string): string {
    return this.config.subcategoryHeadings?.[subcategory] ?? subcategory;
  }

  // Copy to display for the opportuinty's current subcategory
  get subcategoryCopy(): string | undefined {
    if (!this.subcategory) return undefined;
    return this.subcategoryHeadingFor(this.subcategory);
  }

  // The subcategories within the Submitted (or equivalent) tab of this opportunity
  get allSubcategoriesOfSubmitted(): string[] | undefined {
    return this.config.allSubcategoriesOfSubmitted;
  }

  // The possible subcategories of the Submitted status that this opportunity can
  // logically transition INTO from its current state
  get submitMenuOptions(): string[] | undefined {
    // From all the possible options, filter out this opportunity's current subcategory, if any
    const possibleSubcategories =
      this.config.markSubmittedOptionsByTab?.[this.tabTitle()] ??
      this.allSubcategoriesOfSubmitted;
    return possibleSubcategories?.filter(
      (subcategory) => subcategory !== this.subcategory,
    );
  }

  get submittedTabTitle(): OpportunityTab {
    return this.config.submittedTabTitle;
  }

  get deniedTabTitle(): OpportunityTab {
    return this.config.deniedTabTitle;
  }

  get supervisorReviewTabTitle(): OpportunityTab {
    return this.config.supervisorReviewTabTitle;
  }

  /**
   * Returns true when an officer has taken an action requiring supervisor approval and there is
   * not yet a supervisor response for that action.
   */
  get isInSupervisorReview(): boolean {
    return (
      !!this.latestAction &&
      !this.latestAction.isStale &&
      !this.latestAction.supervisorResponse
    );
  }

  get isInSnoozeReview(): boolean {
    return this.isInSupervisorReview && this.latestAction?.type === "DENIAL";
  }

  get isInGrantReview(): boolean {
    return this.isInSupervisorReview && this.latestAction?.type === "APPROVAL";
  }

  get isIndefinitelySnoozed(): boolean {
    return this.denied && !this.manualSnooze && !this.autoSnooze;
  }

  /**
   * The most recent (or current) reviewer.
   * Equivalent to the most recent actionHistory reviewerId if it exists (when type is "APPROVAL")
   * Stored here for easier firestore querying
   */
  get currentReviewerId(): string | undefined {
    return this.updates?.currentReviewerId;
  }

  /**
   * Every reviewer who has ever reviewed this opportunity.
   * Stored here for easier firestore querying (see `allUniqueReviewerIds` array-contains queries).
   */
  get allUniqueReviewerIds(): string[] | undefined {
    return this.updates?.allUniqueReviewerIds;
  }

  /**
   * Returns true when an officer has requested a snooze, and the supervisor
   * has denied that snooze with suggested revisisons to the snooze request.
   *
   * Only relevant for Iowa at this time.
   */
  get isInRevisionsRequested(): boolean {
    return (
      !!this.latestAction &&
      !this.latestAction.isStale &&
      this.latestAction?.type === "DENIAL" &&
      this.latestAction.supervisorResponse?.type === "DENIAL" &&
      !!this.latestAction.supervisorResponse.revisionRequest
    );
  }

  /**
   * Returns true if an officer has requested an opportunity grant, and that request
   * has been approved
   */
  get isGrantApproved(): boolean {
    return (
      !!this.latestAction &&
      !this.latestAction.isStale &&
      this.latestAction?.type === "APPROVAL" &&
      this.latestAction.supervisorResponse?.type === "APPROVAL"
    );
  }

  get grantApprovedTabTitle(): OpportunityTab {
    return this.config.grantApprovedTabTitle;
  }

  get grantApprovedStatusMessage(): string {
    return this.config.grantApprovedStatusMessage;
  }

  tabTitle(category?: OpportunityTabGroup): OpportunityTab {
    if (this.denied) return this.deniedTabTitle;
    if (this.isSubmitted) return this.submittedTabTitle;
    if (this.isInSupervisorReview) return this.supervisorReviewTabTitle;
    if (this.isGrantApproved) return this.grantApprovedTabTitle;
    if (this.almostEligible) return "Almost Eligible";
    if (this.isIneligible) return "Not Currently Eligible";
    return "Eligible Now";
  }

  /*
   * Alert-type opportunities only have a visible status if they're denied; others are always visible
   */
  showEligibilityStatus(component: Component): boolean {
    return !this.config.isAlert || this.reviewStatus === "DENIED";
  }

  criteriaFormatters?: UntypedCriteriaFormatters;

  get requirementsMet(): OpportunityRequirement[] {
    const {
      record,
      config: { eligibleCriteriaCopy },
    } = this;
    if (!record) return [];
    return hydrateUntypedCriteria(
      record.eligibleCriteria,
      eligibleCriteriaCopy,
      this,
      this.criteriaFormatters,
    ).filter((req) => req.text);
  }

  get requirementsAlmostMet(): OpportunityRequirement[] {
    const {
      record,
      isIneligible,
      config: { ineligibleCriteriaCopy, strictlyIneligibleCriteriaCopy },
    } = this;
    if (!record) return [];

    return hydrateUntypedCriteria(
      record.ineligibleCriteria,
      isIneligible
        ? pickBy(
            ineligibleCriteriaCopy,
            (_, key) => !(key in strictlyIneligibleCriteriaCopy),
          )
        : ineligibleCriteriaCopy,
      this,
      this.criteriaFormatters,
    );
  }

  get requirementsNotMet(): OpportunityRequirement[] {
    const {
      record,
      isIneligible,
      config: { strictlyIneligibleCriteriaCopy },
    } = this;
    if (!isIneligible || !record) return [];

    return hydrateUntypedCriteria(
      record.ineligibleCriteria,
      strictlyIneligibleCriteriaCopy,
      this,
      this.criteriaFormatters,
    ).filter((req) => req.text);
  }

  get nonOMSRequirements(): OpportunityRequirement[] {
    const {
      config: { nonOmsCriteria },
    } = this;

    // Because these criteria aren't associated with a referral record, they don't have reasons
    return nonOmsCriteria
      .map((req) =>
        hydrateReq({
          raw: req,
          opportunity: this,
          formatters: this.criteriaFormatters,
        }),
      )
      .filter((req) => req.text);
  }

  /**
   * Returns true if the opportunity record is almost eligible (isAlmostEligible is true).
   */
  get almostEligible(): boolean {
    return this.record.isAlmostEligible;
  }

  /**
   * Returns true if the opportunity record is ineligible (isEligible is false) and not almost eligible
   */
  get isIneligible(): boolean {
    return !this.record.isEligible && !this.almostEligible;
  }

  get isEligible(): boolean {
    return this.record.isEligible;
  }

  get denied(): boolean {
    return !!this.denial;
  }

  get sentryTrackingId(): string {
    return this.record?.opportunityPseudonymizedId ?? null;
  }

  get eligibilityDate(): Date | undefined {
    return this.record?.eligibleDate ?? undefined;
  }

  get almostEligibilityDate(): Date | undefined {
    return this.record?.almostEligibleDate ?? undefined;
  }

  /**
   * Returns the manual max snooze length in days based on the given reasons and their
   * configured max snooze lengths.
   *
   * Undefined if auto snooze is enabled for this opportunity.
   * Undefined if an indefinite snooze reason is selected.
   * Otherwise, returns the maximum max snooze length across the selected reasons,
   * cappedy by the person's release date.
   *
   */
  maxManualSnoozeDays(selectedReasons: string[]): number | undefined {
    // Return undefined for auto snooze.
    if (!this.config.snooze?.maxSnoozeDays) return undefined;

    const selectedMaxSnoozeDays = Object.values(
      pick(this.config.maxSnoozeDaysByDenialReason, selectedReasons),
    );

    if (selectedMaxSnoozeDays.some((maxSnooze) => maxSnooze === undefined))
      // Return undefined if indefinite snooze reason is selected.
      return undefined;
    else {
      // Calculate the maximum max snooze of all selected reasons
      // OR if no reasons are selected, use the default config max.
      const calculatedMaxSnoozeDays =
        selectedReasons.length > 0
          ? Math.max(
              ...selectedMaxSnoozeDays.filter(
                (maxSnooze) => maxSnooze !== undefined,
              ),
            )
          : this.config.snooze?.maxSnoozeDays;

      // Cap the max snooze length to the person's release date.
      return Math.min(
        getPersonDaysToRelease(this.person),
        calculatedMaxSnoozeDays,
      );
    }
  }

  /**
   * Returns the default snooze length in days based on the given reasons. Returns
   * undefined if auto snooze is enabled for this opportunity.
   */
  defaultManualSnoozeDays(denialReasons: string[]): number | undefined {
    const configuredDefaultSnoozeDays = this.config.snooze?.defaultSnoozeDays;
    if (configuredDefaultSnoozeDays === undefined) return;

    const maxManualSnoozeDays = this.maxManualSnoozeDays(denialReasons);
    if (maxManualSnoozeDays === undefined) return configuredDefaultSnoozeDays;

    return Math.min(maxManualSnoozeDays, configuredDefaultSnoozeDays);
  }

  /**
   * The history of officer actions requiring supervisor approval that have been
   * taken on an opportunity .
   */
  get actionHistory(): OfficerRequest[] | undefined {
    return this.updates?.actionHistory;
  }

  get latestAction() {
    return this.actionHistory?.at(-1);
  }

  getPseudoIdFromReviewerId(
    reviewerId: string | undefined,
  ): string | undefined {
    if (!reviewerId) return undefined;
    return this.rootStore.workflowsStore.availableOfficersWithOrWithoutCaseloads.find(
      (s) => s.staffExternalId === reviewerId,
    )?.pseudonymizedId;
  }

  /**
   * Push a new officer action requiring supervisor approval onto the action history
   * timeline.
   */
  async setOfficerAction(
    officerActionParams: OfficerApprovalRequest | OfficerDenialRequest,
  ): Promise<void> {
    // If a client is moving into review, we should
    // delete denials and submissions, if applicable.
    if (this.denied) {
      await this.deleteOpportunityDenialAndSnooze();
    }
    if (this.isSubmitted) {
      await this.deleteSubmitted();
    }

    const officerAction = {
      date: Timestamp.fromDate(new Date()),
      by: this.userName,
      updateById: this.userId,
      isStale: false,
      ...officerActionParams,
    };

    const assignedReviewerId = this.getPseudoIdFromReviewerId(
      this.currentReviewerId,
    );
    const currentReviewerId =
      officerActionParams.type === "APPROVAL"
        ? officerActionParams.reviewerId
        : undefined;

    const updatedActionHistory = (this.actionHistory ?? []).concat(
      officerAction,
    );

    const actionMetadata: OpportunityApprovalActionsMetadata["action"] =
      officerAction.type === "DENIAL"
        ? {
            type: officerAction.type,
            actionPlan: officerAction.actionPlan,
            requestedDenialReasons: officerAction.denialReasons,
          }
        : { type: officerAction.type, additionalNotes: officerAction.notes };

    const originalStatus = this.reviewStatus;

    await this.rootStore.firestoreStore.updateOpportunityActionHistory({
      opportunity: this,
      actionHistory: updatedActionHistory,
      currentReviewerId,
      currentUserId: currentReviewerId ? this.userId : undefined,
      stateCode: this.person.stateCode,
    });

    this.rootStore.analyticsStore.trackOpportunityApprovalActions({
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
      staffId: this.rootStore.userStore.userPseudoId ?? this.currentUserEmail,
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      action: actionMetadata,
      currentStatus: originalStatus,
      subsequentStatus: this.reviewStatus,
      assignedReviewerPseudoId: assignedReviewerId,
      nextReviewerPseudoId: this.getPseudoIdFromReviewerId(currentReviewerId),
    });
  }

  /**
   * Mark the latest officer action as stale. A stale action should not be relied upon
   * when determining client status, as staleness signifies that some other action has
   * broken the client out of the supervisor approval cycle. Some examples of when an
   * officer action should be marked stale are:
   *   - when a client is successfully snoozed
   *   - when an indefinite snooze is denied
   */
  async markActionHistoryStale(): Promise<void> {
    if (this.actionHistory && this.latestAction && !this.latestAction.isStale) {
      const originalStatus = this.reviewStatus;

      const updatedOfficerAction = {
        ...this.latestAction,
        isStale: true,
      };

      const updatedActionHistory = this.actionHistory
        .slice(0, -1)
        .concat(updatedOfficerAction);

      await this.rootStore.firestoreStore.updateOpportunityActionHistory({
        opportunity: this,
        actionHistory: updatedActionHistory,
        stateCode: this.person.stateCode,
      });

      this.rootStore.analyticsStore.trackOpportunityApprovalActions({
        opportunityType: this.type,
        opportunityId: this.sentryTrackingId,
        staffId: this.rootStore.userStore.userPseudoId ?? this.currentUserEmail,
        justiceInvolvedPersonId: this.person.pseudonymizedId,
        currentStatus: originalStatus,
        subsequentStatus: this.reviewStatus,
      });
    }
  }

  /**
   * Clears all actions requiring supervisor approval.
   */
  async deleteActionHistory(): Promise<void> {
    const originalStatus = this.reviewStatus;

    const assignedReviewerId = this.getPseudoIdFromReviewerId(
      this.currentReviewerId,
    );

    await this.rootStore.firestoreStore.deleteOpportunityActionHistory(this);
    this.rootStore.analyticsStore.trackOpportunityApprovalActions({
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
      staffId: this.rootStore.userStore.userPseudoId ?? this.currentUserEmail,
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      currentStatus: originalStatus,
      subsequentStatus: this.reviewStatus,
      assignedReviewerPseudoId: assignedReviewerId,
      nextReviewerPseudoId: undefined,
    });
  }

  /**
   * Set the supervisor's `DENIAL`, `APPROVAL`, or `REVISION` of the latest officer request.
   */
  async setSupervisorResponse(
    supervisorResponseParams: Omit<SupervisorResponse, "date" | "by">,
  ): Promise<void> {
    const supervisorResponse = {
      date: Timestamp.fromDate(new Date()),
      by: this.userName,
      updateById: this.userId,
      ...supervisorResponseParams,
    } as SupervisorResponse;

    if (!this.actionHistory || !this.latestAction) {
      throw new Error(
        `Supervisor with id [${this.rootStore.userStore.userPseudoId}] cannot respond when there is no existing Action History for client with id [${this.person.pseudonymizedId}]`,
      );
    }

    const actionMetadata: OpportunityApprovalActionsMetadata["action"] = {
      type: this.latestAction.type,
      supervisorResponseType: supervisorResponse.type,
      revisionRequest:
        supervisorResponse.type === "DENIAL"
          ? supervisorResponse.revisionRequest
          : undefined,
    };

    const originalStatus = this.reviewStatus;

    const updatedOfficerAction = {
      ...this.latestAction,
      supervisorResponse,
    };

    const updatedActionHistory = this.actionHistory
      .slice(0, -1)
      .concat(updatedOfficerAction);

    const assignedReviewerId = this.getPseudoIdFromReviewerId(
      this.currentReviewerId,
    );
    const currentReviewerId =
      supervisorResponse.type === "REVISION"
        ? supervisorResponse.reviewerId
        : undefined;

    await this.rootStore.firestoreStore.updateOpportunityActionHistory({
      opportunity: this,
      actionHistory: updatedActionHistory,
      currentReviewerId,
      currentUserId: currentReviewerId ? this.userId : undefined,
      stateCode: this.person.stateCode,
    });

    this.rootStore.analyticsStore.trackOpportunityApprovalActions({
      opportunityType: this.type,
      opportunityId: this.sentryTrackingId,
      staffId: this.rootStore.userStore.userPseudoId ?? this.currentUserEmail,
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      action: actionMetadata,
      currentStatus: originalStatus,
      subsequentStatus: this.reviewStatus,
      assignedReviewerPseudoId: assignedReviewerId,
      nextReviewerPseudoId: this.getPseudoIdFromReviewerId(currentReviewerId),
    });
  }

  get userName(): string {
    // We'll fall back to the user's email
    return this.rootStore.userStore.userFullName ?? this.currentUserEmail;
  }

  get userId(): string | undefined {
    return this.rootStore.userStore.externalId;
  }

  get denialViewPrompt(): string {
    return this.config.isAlert
      ? `Please select the reason(s) ${this.person?.displayPreferredName} should be overridden:`
      : `Which of the following requirements has ${this.person?.displayPreferredName} not met${this.instanceDetails ? ` [${this.instanceDetails}]` : ""}?`;
  }

  get snoozeReviewStatusMessage(): string {
    return this.config.snoozeReviewStatusMessage;
  }

  get grantReviewStatusMessage(): string {
    return this.config.grantReviewStatusMessage;
  }

  // ===============================
  // properties below this line are stubs and in most cases should be overridden
  // in a subclass. Given their triviality they are not annotated by MobX either,
  // so subclasses can use normal annotations instead of having to use `override`.
  // ===============================

  get indefiniteDenialReasons(): DenialReasonsMap {
    return this.config.indefiniteDenialReasons;
  }

  get formVariant(): FormVariant | undefined {
    return undefined;
  }

  get eligibleStatusMessage(): string | undefined {
    return undefined;
  }

  get almostEligibleStatusMessage(): string | undefined {
    return undefined;
  }

  // Used to optionally display an opportunity-specific id in OpportunityCapsule
  get instanceDetails(): string | undefined {
    return undefined;
  }

  get highlightCalloutText(): string {
    throw Error(`Implement highlightCalloutText for ${this.type}`);
  }

  // Used to optionally display information appended to an opportunity label
  get labelAddendum(): string | undefined {
    return undefined;
  }

  // Used to implement custom logic for the palette to use for this opportunity
  get customStatusPalette(): StatusPalette | undefined {
    return undefined;
  }

  // Used to get opportunities to be concurrently snoozed when the primary opportunity is snoozed
  get snoozeCompanionOpportunities() {
    if (!this.rootStore.userStore.activeFeatureVariants.snoozeCompanions) {
      return [];
    }

    const snoozeCompanionOpportunityTypes =
      this.config.snoozeCompanionOpportunityTypes ?? [];
    return this.person.flattenedOpportunities.filter((opp) =>
      snoozeCompanionOpportunityTypes.includes(opp.type),
    );
  }

  // Used to determine whether or not an included opportunity requires a "Revert Changes" confirmation step
  requiresRevertConfirmation = false;

  // Returns custom copy for the "Revert Changes" confirmation step
  revertConfirmationCopy: RevertConfirmationCopy = {
    headerText: "Are you sure you want to revert changes?",
    descriptionText: "This action cannot be undone.",
  };

  // TODO(#9952): Clean up custom behavior related to supervisor approvals. Remove this handler.
  // Used to execute additional opportunity-specific actions when the "Revert Changes" confirmation is triggered
  async handleAdditionalUndoActions(): Promise<void> {
    return undefined;
  }

  // Used to supplement the default revert link display logic and show the "Revert Changes" link based on opportunity-specific
  // custom logic when default conditions fail
  get showRevertLinkFallback(): boolean {
    return false;
  }

  get caseNoteHeaders() {
    return this.config.caseNoteHeaders;
  }

  get caseNotesTitle() {
    return this.config.caseNotesTitle;
  }

  eligibilityStatusLabel(
    includeReasons?: boolean,
    category?: OpportunityTabGroup,
  ): string | null {
    const {
      almostEligible,
      almostEligibleStatusMessage,
      eligibleStatusMessage,
      defaultEligibility,
      denial,
      isSubmitted,
      config: { isAlert, submittedTabTitle },
      isInSnoozeReview,
      isInGrantReview,
      grantReviewStatusMessage,
      snoozeReviewStatusMessage,
      isIndefinitelySnoozed,
      isGrantApproved,
      grantApprovedStatusMessage,
    } = this;

    if (!isHydrated(this)) return null;

    if (denial?.reasons.length) {
      const statusText = isAlert
        ? "Override"
        : `${isIndefinitelySnoozed ? "Indefinitely" : "Currently"} ineligible`;
      const withReasons = includeReasons
        ? ` (${denial.reasons.join(", ")})`
        : "";

      return `${statusText}${withReasons}`;
    }

    if (isSubmitted) {
      if (this.isPendingOverdue) {
        return "Pending: Overdue";
      }
      return submittedTabTitle;
    }

    if (this.isIneligible) return "Ineligible";

    if (isGrantApproved) {
      return grantApprovedStatusMessage;
    }

    if (isInSnoozeReview) {
      return snoozeReviewStatusMessage;
    }

    if (isInGrantReview) {
      return grantReviewStatusMessage;
    }

    if (almostEligible) {
      return includeReasons && almostEligibleStatusMessage
        ? almostEligibleStatusMessage
        : "Almost eligible";
    }

    if (defaultEligibility === "MAYBE") return "May be eligible";

    return eligibleStatusMessage ?? "Eligible";
  }

  // This is the latest time that the person "completed" this opportunity.
  // If the opportunity was was marked submitted before then,
  // updateOpportunityEligibility will clear that status.
  get latestTransitionDate(): Date | undefined {
    return undefined;
  }
}
